import { Box, Divider, Grid } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormikProps, FormikTouched } from 'formik';
import toast from 'react-hot-toast';
import CustomStepper from '../Common/CustomStepper';
import StyledButton from '../Common/StyledButton';
import LabeledInput from '../Common/LabeledInput';
import CustomMultiSelect from '../CustomMultiSelect/CustomMultiSelect';
import { sanitizeInputName } from '../../utils/userInputUtils';
import AddClientGroupMembersDataGrid from './AddMembersDataGrid';
import { IGroupClientFormValues, StaffMember } from '../../utils/validations/clientDetailsForm';
import { StyledFormHelperText } from '../ClientDetail/ClientDetailForm';
import { toastStyles } from '../../utils/utils';

interface AddClientGroupProps {
  formik: FormikProps<IGroupClientFormValues>;
  triggerTextChange: (_e: React.ChangeEvent<HTMLInputElement>) => void;
  handleTagsChange: (_newValue: (string | object)[]) => void;
  handleKeyDown: (_event: React.KeyboardEvent<HTMLInputElement>) => void;
  handleObjectivesBlur: (_e: React.FocusEvent<HTMLDivElement, Element>) => void;
  handleStaffTagsChange: (_newValue: (string | object)[]) => void;
  loadMoreOptions: () => void;
  options: StaffMember[];
  fixedOptions: StaffMember[];
  objectivesInputRef: React.RefObject<HTMLInputElement>;
  onStepComplete: (_groupName: string, _step: number) => void;
  activeStep: number;
}

export default function AddClientGroup({
  formik,
  triggerTextChange,
  handleTagsChange,
  handleKeyDown,
  handleObjectivesBlur,
  handleStaffTagsChange,
  loadMoreOptions,
  options,
  fixedOptions,
  objectivesInputRef,
  onStepComplete,
  activeStep,
}: AddClientGroupProps) {
  const { t } = useTranslation();
  const steps = [t('addNewClient.groupInfo'), t('addNewClient.addMembers')];

  const handleNameChange = (field: string, value: string) => {
    formik.handleChange(field)(sanitizeInputName(value, true));
  };

  const handleDescriptionChange = (field: string, value: string) => {
    formik.setFieldValue(field, value);
  };

  const handleNextClick = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      const { groupName } = formik.values;
      if (onStepComplete) {
        onStepComplete(groupName, 1); // Go to step 2
      }
    } else {
      formik.setTouched(errors as FormikTouched<IGroupClientFormValues>);
    }
  };

  const handlePrevClick = () => {
    const { groupName } = formik.values;
    onStepComplete(groupName, 0);
  };

  const handleAddGroup = async () => {
    if (formik.values.members.length > 20) {
      toast.error(t('addNewClient.validations.membersLimit'), toastStyles.error);
      return;
    }
    const errors = await formik.validateForm();

    if (Object.keys(errors).length === 0) {
      formik.handleSubmit();
    } else {
      formik.setTouched(errors as FormikTouched<IGroupClientFormValues>);
    }
  };

  const getObjectivesError = () => {
    if (!formik?.touched?.objectives) return '';
    if (!formik?.errors?.objectives) return '';
    if (!Array.isArray(formik.errors.objectives)) return t(formik.errors.objectives);
    const error = formik.errors.objectives.filter((i) => !!i);
    return error[0];
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-start" p={1} width="100%">
        <Box width="40%">
          <CustomStepper steps={steps} activeStep={activeStep} />
        </Box>
      </Box>

      {activeStep === 0 && (
        <form onSubmit={formik.handleSubmit} noValidate>
          <Grid container spacing={2} flexDirection="column">
            {/* First Row: Name and Birthday */}
            <Grid item sm={6}>
              <LabeledInput
                label={t('addNewClient.groupName')}
                id="group-name"
                type="text"
                name="groupName"
                value={formik.values.groupName}
                handleChange={(e) => handleNameChange('groupName', e.target.value)}
                onBlur={formik.handleBlur}
                error={formik.touched.groupName && formik.errors.groupName}
                placeholder={t('addNewClient.groupName')}
              />
            </Grid>

            {/* Second Row: Description */}
            <Grid item sm={6}>
              <LabeledInput
                label={t('addNewClient.description')}
                id="description"
                type="text"
                value={formik.values.description}
                handleChange={(e) => handleDescriptionChange('description', e.target.value)}
                onBlur={formik.handleBlur}
                error={formik.touched.description && formik.errors.description}
                placeholder={t('addNewClient.description')}
                multiline
                minRows={3}
              />
            </Grid>

            {/* Objectives */}
            <Grid item xs={12}>
              <CustomMultiSelect
                onTextFieldChange={triggerTextChange}
                options={[]}
                value={formik.values.objectives}
                onChange={handleTagsChange}
                onKeyDown={handleKeyDown}
                onBlur={handleObjectivesBlur}
                label={t('addNewClient.objectives')}
                placeholder={t('addNewClient.objectivesData')}
                inputRef={objectivesInputRef}
                freeSolo
              />
              <StyledFormHelperText error>{t(getObjectivesError())}</StyledFormHelperText>
            </Grid>

            {/* Staff Members */}
            <Grid item xs={12}>
              <CustomMultiSelect
                options={options}
                value={formik.values.staffMembers}
                onChange={handleStaffTagsChange}
                loadMoreOptions={loadMoreOptions}
                label={t('addNewClient.staffMembers')}
                freeSolo={false}
                fixedOptions={fixedOptions}
                withIcon
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="flex-end">
            <StyledButton onClick={handleNextClick}>{t('addNewClient.next')}</StyledButton>
          </Box>
        </form>
      )}

      {activeStep === 1 && (
        <AddClientGroupMembersDataGrid
          formik={formik}
          onPrev={handlePrevClick}
          onAddGroup={handleAddGroup}
        />
      )}
    </>
  );
}
